import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router-dom";
// Actions
import {
  SG_GET_TASK_SCHEDULES,
  SG_GET_TASK_TEMPLATES,
  SG_GET_TASK_TEMPLATE_SELECT,
} from "constants/actions";

// Pulse Survey Context
import { usePulseSurveys, PulseSurveysProvider } from "app/audit/AI/PulseSurvey_V2/Data/LandingPageContext";

// Schedule Context for non-pulse surveys
import { useScheduleContext, ScheduleProvider } from "./ScheduleContext";

// Icons (simplified inline SVG components)
const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.7L16.2,16.2Z"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const EngagementIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
  </svg>
);

const PulseIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V10.5A1,1 0 0,0 4,11.5H16A1,1 0 0,0 17,10.5M20.5,6C19,6 17.7,6.9 17.1,8.2A1,1 0 0,0 18,9A2,2 0 0,1 20,11A2,2 0 0,1 18,13A1,1 0 0,0 17.1,13.8C17.7,15.1 19,16 20.5,16A3.5,3.5 0 0,0 24,12.5A3.5,3.5 0 0,0 20.5,6M7.5,2C6,2 4.7,2.9 4.1,4.2A1,1 0 0,0 5,5A2,2 0 0,1 7,7A2,2 0 0,1 5,9A1,1 0 0,0 4.1,9.8C4.7,11.1 6,12 7.5,12A3.5,3.5 0 0,0 11,8.5A3.5,3.5 0 0,0 7.5,2Z"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z"/>
  </svg>
);

// Mock data - replace with actual data from Redux/API
// Temporarily commented out - will be replaced with real pulse survey data
// const mockActiveSurveys = [
//   {
//     id: 1,
//     title: "Q4 Employee Engagement Survey",
//     type: "Culture Audit",
//     status: "active",
//     startDate: "2024-01-15",
//     endDate: "2024-01-29",
//     participants: 156,
//     responses: 89
//   },
//   {
//     id: 2,
//     title: "Leadership Effectiveness Review",
//     type: "360 Review",
//     status: "scheduled",
//     startDate: "2024-02-01",
//     endDate: "2024-02-15",
//     participants: 24,
//     responses: 0
//   },
//   {
//     id: 3,
//     title: "DEI Pulse Check",
//     type: "EDI Survey",
//     status: "active",
//     startDate: "2024-01-20",
//     endDate: "2024-02-05",
//     participants: 203,
//     responses: 145
//   }
// ];

// const mockHistoricalSurveys = [
//   {
//     id: 4,
//     title: "Q3 Team Dynamics Assessment",
//     type: "Culture Audit",
//     status: "completed",
//     startDate: "2023-12-01",
//     endDate: "2023-12-15",
//     participants: 178,
//     responses: 156,
//     completionRate: "87.6%"
//   },
//   {
//     id: 5,
//     title: "Annual Culture Survey 2023",
//     type: "Culture Audit",
//     status: "completed",
//     startDate: "2023-10-15",
//     endDate: "2023-11-15",
//     participants: 245,
//     responses: 201,
//     completionRate: "82.0%"
//   },
//   {
//     id: 6,
//     title: "Manager Feedback Survey",
//     type: "360 Review",
//     status: "completed",
//     startDate: "2023-11-01",
//     endDate: "2023-11-10",
//     participants: 45,
//     responses: 42,
//     completionRate: "93.3%"
//   }
// ];

// Helpers to safely read numeric counts from mixed sources (schedule vs pulse)
const toNum = (val, fallback = 0) => {
  const n = typeof val === 'string' ? Number(val) : val;
  return Number.isFinite(n) ? n : fallback;
};

const getCounts = (survey) => {
  // Prefer top-level mapped fields; fall back to rawData when present
  const participants = toNum(
    survey?.participants ?? survey?.rawData?.total_requests,
    0
  );
  const responses = toNum(
    survey?.responses ?? survey?.rawData?.total_responses,
    0
  );
  return { participants, responses };
};

const SurveySchedulingContent = () => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [activeTab, setActiveTab] = useState("active");
  const [showSurveyTypeModal, setShowSurveyTypeModal] = useState(false);
  const dispatch = useDispatch();

  // Get pulse survey data from context - all pre-filtered by provider
  const { templatesData, draftSurveys, activeSurveys, completedSurveys } =
    usePulseSurveys();

  // Get non-pulse survey data from schedule context
  const {
    getCurrentSurveys,
    formatSurveyData,
    fetchActiveSurveys,
    fetchHistoricalSurveys,
    loading: scheduleLoading,
  } = useScheduleContext();

  useEffect(() => {
    dispatch({ type: SG_GET_TASK_TEMPLATE_SELECT });
    dispatch({ type: SG_GET_TASK_SCHEDULES });
    dispatch({ type: SG_GET_TASK_TEMPLATES });

    // Fetch non-pulse survey data on component mount
    fetchActiveSurveys();
  }, [dispatch, fetchActiveSurveys]);

  // Fetch appropriate data when tab changes
  useEffect(() => {
    if (activeTab === "historical") {
      fetchHistoricalSurveys();
    } else {
      fetchActiveSurveys();
    }
  }, [activeTab, fetchActiveSurveys, fetchHistoricalSurveys]);

  // Helper function to transform survey data to ScheduleV2 format
  const transformSurveyData = (surveys, defaultStatus) => {
    return (
      surveys?.map((survey) => {
        // Use the default status passed in - no filtering logic here
        const status = defaultStatus;

        return {
          id: survey.id,
          title: survey.name,
          type: "Pulse Survey",
          status: status,
          startDate: null, // Pulse surveys don't have scheduled dates
          endDate: null,
          participants: 0, // Will need to get from assigned employees
          responses: 0, // Will need actual response count
          completionRate: status === "completed" ? "100%" : null,
          updatedAt: survey.updatedValue,
        };
      }) || []
    );
  };

  // Get non-pulse survey data
  const currentNonPulseSurveys = getCurrentSurveys();
  const formattedNonPulseSurveys = formatSurveyData(currentNonPulseSurveys);

  // Transform the pre-filtered pulse data from context - NO FILTERING, just transform
  // const transformedTemplates = transformSurveyData(templatesData, "scheduled");
  const transformedPulseDrafts = transformSurveyData(draftSurveys, "draft");
  const transformedPulseActive = transformSurveyData(activeSurveys, "active");
  const transformedPulseCompleted = transformSurveyData(
    completedSurveys,
    "completed"
  );

  // Split non-pulse surveys by status
  const activeNonPulseSurveys = formattedNonPulseSurveys.filter((survey) => {
    const status = (survey.status || "").toLowerCase();
    return ["pending", "live", "processing", "demo"].includes(status);
  });
  const completedNonPulseSurveys = formattedNonPulseSurveys.filter((survey) => {
    const status = (survey.status || "").toLowerCase();
    return ["completed"].includes(status);
  });

  console.log(formattedNonPulseSurveys, activeNonPulseSurveys);
  // Combine pulse and non-pulse surveys for display
  const activeDisplaySurveys = [
    ...transformedPulseDrafts,
    ...transformedPulseActive,
    ...activeNonPulseSurveys,
  ];
  const historicalDisplaySurveys = [
    ...transformedPulseCompleted,
    ...completedNonPulseSurveys,
  ];

  // Handle scheduling new survey - show modal for survey type selection
  const handleScheduleSurvey = () => {
    setShowSurveyTypeModal(true);
  };

  // Handle survey type selection
  const handleSurveyTypeSelect = (surveyType) => {
    setShowSurveyTypeModal(false);

    if (surveyType === "engagement") {
      // Navigate to engagement survey scheduling
      navigate("/leader/audit/schedule-v2/new");
    } else if (surveyType === "pulse") {
      // Navigate to pulse survey library
      navigate("/leader/audit/schedule-v2/pulse");
    }
  };

  // Close survey type modal
  const closeSurveyTypeModal = () => {
    setShowSurveyTypeModal(false);
  };

  // Handle survey row click
  const handleSurveyClick = (survey) => {
    if (survey.type === "Pulse Survey") {
      if (survey.status === "draft") {
        // Navigate to edit draft pulse survey
        navigate(`/leader/audit/schedule-v2/pulse/edit/${survey.id}`);
      } else if (survey.status === "active") {
        // Navigate to view active pulse survey
        navigate(`/leader/audit/schedule-v2/pulse/manage/${survey.id}`);
      } else if (survey.status === "completed") {
        // Navigate to view completed pulse survey results
        navigate(`/leader/audit/schedule-v2/pulse/view/${survey.id}`);
      } else {
        // Scheduled pulse survey - navigate to edit
        navigate(`/leader/audit/schedule-v2/pulse/edit/${survey.id}`);
      }
    } else if (survey.type === "Culture Audit") {
      // Handle Culture Audit surveys (non-pulse)
      if (["pending", "live", "processing", "demo"].includes(survey.status)) {
        // Navigate to manage active culture audit survey
        navigate(`/leader/audit/schedule-v2/manage/${survey.id}`);
      } else if (["completed"].includes(survey.status)) {
        // Navigate to view completed culture audit survey results
        navigate(`/leader/audit/schedule-v2/results/${survey.id}`);
      } else {
        // Default fallback for other statuses
        console.log("Culture audit survey clicked:", survey);
      }
    } else {
      // Handle other survey types (engagement, 360, etc.)
      if (survey.status === "active") {
        // Navigate to manage active survey
        navigate(`/leader/audit/schedule-v2/manage/${survey.id}`);
      } else {
        // Navigate to survey results or details
        console.log("Survey clicked:", survey);
        // Example: navigate(`/leader/audit/surveys/${survey.id}/results`);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Get status badge component
  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <StatusBadge status="active">
            <CheckIcon /> Active
          </StatusBadge>
        );
      case "scheduled":
        return (
          <StatusBadge status="scheduled">
            <ClockIcon /> Scheduled
          </StatusBadge>
        );
      case "draft":
        return (
          <StatusBadge status="draft">
            <ClockIcon /> Draft
          </StatusBadge>
        );
      case "completed":
        return (
          <StatusBadge status="completed">
            <CheckIcon /> Completed
          </StatusBadge>
        );
      case "demo":
        return (
          <StatusBadge status="demo">
            <CheckIcon /> Demo
          </StatusBadge>
        );
      default:
        return <StatusBadge status="default">{status}</StatusBadge>;
    }
  };

  return (
    <Container>
      {/* Header Section */}
      <HeaderSection>
        <HeaderContent>
          <IconWrapper>
            <CalendarIcon />
          </IconWrapper>
          <HeaderText>
            <Title>Survey Scheduling</Title>
            <Subtitle>Manage active surveys and view historical data</Subtitle>
          </HeaderText>
        </HeaderContent>

        <ScheduleButton onClick={handleScheduleSurvey}>
          <ButtonIcon>
            <PlusIcon />
          </ButtonIcon>
          <ButtonText>Schedule Survey</ButtonText>
        </ScheduleButton>
      </HeaderSection>

      {/* Tab Navigation */}
      <TabSection>
        <TabNavigation>
          <TabButton
            active={activeTab === "active"}
            onClick={() => setActiveTab("active")}
          >
            Active Surveys ({activeDisplaySurveys.length})
          </TabButton>
          <TabButton
            active={activeTab === "historical"}
            onClick={() => setActiveTab("historical")}
          >
            Historical Surveys ({historicalDisplaySurveys.length})
          </TabButton>
        </TabNavigation>
      </TabSection>

      {/* Content Section */}
      <ContentSection>
        {activeTab === "active" ? (
          <TableSection>
            {/* <TableHint>
              💡 Click on any survey to view details and manage settings
            </TableHint> */}
            {activeDisplaySurveys.length > 0 ? (
              <SurveysTable>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>Survey Details</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Duration</TableHeaderCell>
                    <TableHeaderCell>Progress</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {activeDisplaySurveys.map((survey, index) => (
                    <ClickableTableRow
                      key={survey.id}
                      index={index}
                      onClick={() => handleSurveyClick(survey)}
                      title="Click to view survey details"
                    >
                      <TableCell>
                        <SurveyInfo>
                          <SurveyTitle>{survey.title}</SurveyTitle>
                          <SurveyType>{survey.type}</SurveyType>
                        </SurveyInfo>
                      </TableCell>
                      <TableCell>{getStatusBadge(survey.status)}</TableCell>
                      <TableCell>
                        <DateRange>
                          {survey.startDate && survey.endDate ? (
                            <DateText>
                              {formatDate(survey.startDate)} -{" "}
                              {formatDate(survey.endDate)}
                            </DateText>
                          ) : (
                            <DateText>
                              Updated: {formatDate(survey.updatedAt)}
                            </DateText>
                          )}
                        </DateRange>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          if (survey.status === "draft") {
                            return (
                              <ProgressInfo>
                                <ProgressText>Draft - Not sent</ProgressText>
                                <ProgressBar>
                                  <ProgressFill percentage={0} />
                                </ProgressBar>
                              </ProgressInfo>
                            );
                          }

                          const { participants, responses } = getCounts(survey);
                          const pct =
                            participants > 0
                              ? (responses / participants) * 100
                              : 0;

                          return (
                            <ProgressInfo>
                              <ProgressText>
                                {responses} / {participants} responses
                              </ProgressText>
                              <ProgressBar>
                                <ProgressFill percentage={pct} />
                              </ProgressBar>
                            </ProgressInfo>
                          );
                        })()}
                      </TableCell>
                    </ClickableTableRow>
                  ))}
                </TableBody>
              </SurveysTable>
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <CalendarIcon />
                </EmptyIcon>
                <EmptyTitle>No Active Surveys</EmptyTitle>
                <EmptyText>
                  Schedule your first survey to start collecting valuable
                  feedback from your team.
                </EmptyText>
                <EmptyButton onClick={handleScheduleSurvey}>
                  <PlusIcon />
                  Schedule First Survey
                </EmptyButton>
              </EmptyState>
            )}
          </TableSection>
        ) : (
          <TableSection>
            {/* <TableHint>📊 View completed surveys and their results</TableHint> */}
            {historicalDisplaySurveys.length > 0 ? (
              <SurveysTable>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>Survey Details</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Duration</TableHeaderCell>
                    <TableHeaderCell>Completion Rate</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {historicalDisplaySurveys.map((survey, index) => (
                    <ClickableTableRow
                      key={survey.id}
                      index={index}
                      onClick={() => handleSurveyClick(survey)}
                      title="Click to view survey results"
                    >
                      <TableCell>
                        <SurveyInfo>
                          <SurveyTitle>{survey.title}</SurveyTitle>
                          <SurveyType>{survey.type}</SurveyType>
                        </SurveyInfo>
                      </TableCell>
                      <TableCell>{getStatusBadge(survey.status)}</TableCell>
                      <TableCell>
                        <DateRange>
                          {survey.startDate && survey.endDate ? (
                            <DateText>
                              {formatDate(survey.startDate)} -{" "}
                              {formatDate(survey.endDate)}
                            </DateText>
                          ) : (
                            <DateText>
                              Updated: {formatDate(survey.updatedAt)}
                            </DateText>
                          )}
                        </DateRange>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const { participants, responses } = getCounts(survey);
                          const computedRate =
                            participants > 0
                              ? `${Math.round(
                                  (responses / participants) * 100
                                )}%`
                              : "0%";
                          return (
                            <CompletionInfo>
                              <CompletionRate>
                                {survey.completionRate || computedRate}
                              </CompletionRate>
                              <CompletionText>
                                {responses} / {participants} responses
                              </CompletionText>
                            </CompletionInfo>
                          );
                        })()}
                      </TableCell>
                    </ClickableTableRow>
                  ))}
                </TableBody>
              </SurveysTable>
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <CalendarIcon />
                </EmptyIcon>
                <EmptyTitle>No Historical Surveys</EmptyTitle>
                <EmptyText>
                  Completed surveys will appear here once you've finished
                  running them.
                </EmptyText>
              </EmptyState>
            )}
          </TableSection>
        )}
      </ContentSection>

      {/* Survey Type Selection Modal */}
      {showSurveyTypeModal && (
        <ModalOverlay onClick={closeSurveyTypeModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Choose Survey Type</ModalTitle>
              <CloseButton onClick={closeSurveyTypeModal}>
                <CloseIcon />
              </CloseButton>
            </ModalHeader>

            <ModalContent>
              <ModalSubtitle>
                Select the type of survey you'd like to create
              </ModalSubtitle>

              <SurveyTypeOptions>
                <SurveyTypeOption
                  onClick={() => handleSurveyTypeSelect("engagement")}
                >
                  <SurveyTypeIconWrapper primary>
                    <EngagementIcon />
                  </SurveyTypeIconWrapper>
                  <SurveyTypeContent>
                    <SurveyTypeTitle>
                      Schedule Engagement Survey
                    </SurveyTypeTitle>
                    <SurveyTypeDescription>
                      Comprehensive survey for in-depth employee feedback and
                      culture assessment
                    </SurveyTypeDescription>
                  </SurveyTypeContent>
                  <SurveyTypeArrow>
                    <ArrowRightIcon />
                  </SurveyTypeArrow>
                </SurveyTypeOption>

                <SurveyTypeOption
                  onClick={() => handleSurveyTypeSelect("pulse")}
                >
                  <SurveyTypeIconWrapper secondary>
                    <PulseIcon />
                  </SurveyTypeIconWrapper>
                  <SurveyTypeContent>
                    <SurveyTypeTitle>Send Pulse Survey</SurveyTypeTitle>
                    <SurveyTypeDescription>
                      Quick, focused survey for rapid feedback and real-time
                      insights
                    </SurveyTypeDescription>
                  </SurveyTypeContent>
                  <SurveyTypeArrow>
                    <ArrowRightIcon />
                  </SurveyTypeArrow>
                </SurveyTypeOption>
              </SurveyTypeOptions>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
};

// Wrap the main component with both providers
const SurveyScheduling = () => (
  <ScheduleProvider>
    <PulseSurveysProvider>
      <SurveySchedulingContent />
    </PulseSurveysProvider>
  </ScheduleProvider>
);

export default SurveyScheduling;

// Styled Components - Scaled down from the reference
const Container = styled.div`
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 7px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 28px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const ScheduleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonText = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: white;
`;

const TabSection = styled.div`
  margin-bottom: 20px;
`;

const TabNavigation = styled.div`
  display: flex;
  gap: 2px;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 8px;
  width: fit-content;
`;

const TabButton = styled.button`
  padding: 10px 20px;
  background: ${(props) => (props.active ? "#476dfa" : "transparent")};
  border: none;
  border-radius: 6px;
  color: ${(props) => (props.active ? "white" : "#7e7e7e")};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${(props) => (props.active ? "#3d5ce6" : "#e9ecef")};
  }
`;

const ContentSection = styled.div``;

const TableSection = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TableHint = styled.div`
  padding: 10px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-family: "Poppins";
  font-size: 12px;
  color: #7e7e7e;
  text-align: center;
`;

const SurveysTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 14px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const TableBody = styled.tbody``;

const ClickableTableRow = styled.tr`
  cursor: pointer;
  transition: all 0.2s;
  animation: slideInRow 0.3s ease-out;
  animation-fill-mode: both;

  &:hover {
    background: #f0f4ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(71, 109, 250, 0.08);
  }

  @keyframes slideInRow {
    from {
      opacity: 0;
      transform: translateX(-16px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  ${(props) =>
    props.index &&
    `
    animation-delay: ${props.index * 0.04}s;
  `}
`;

const TableCell = styled.td`
  padding: 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const SurveyInfo = styled.div``;

const SurveyTitle = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const SurveyType = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  background: #f0f4ff;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;

  ${(props) => {
    switch (props.status) {
      case "active":
        return `
          background: #e8f5e8;
          color: #2e7d2e;
        `;
      case "scheduled":
        return `
          background: #fff4e6;
          color: #cc8500;
        `;
      case "draft":
        return `
          background: #f0f4ff;
          color: #476dfa;
        `;
      case "completed":
        return `
          background: #e3f2fd;
          color: #1976d2;
        `;
      case "demo":
        return `
          background: #e3f2fd;
          color: #1976d2;
        `;
      default:
        return `
          background: #f0f0f0;
          color: #666;
        `;
    }
  }}
`;

const DateRange = styled.div``;

const DateText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const ProgressInfo = styled.div``;

const ProgressText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin-bottom: 6px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #476dfa;
  border-radius: 3px;
  width: ${props => Math.min(props.percentage, 100)}%;
  transition: width 0.3s ease;
`;

const CompletionInfo = styled.div``;

const CompletionRate = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #2e7d2e;
  margin-bottom: 2px;
`;

const CompletionText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0 0 20px 0;
  max-width: 350px;
  margin-left: auto;
  margin-right: auto;
`;

const EmptyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #7e7e7e;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

const ModalContent = styled.div`
  padding: 20px 24px 24px 24px;
`;

const ModalSubtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0 0 24px 0;
  text-align: center;
`;

const SurveyTypeOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SurveyTypeOption = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;

  &:hover {
    border-color: #476dfa;
    box-shadow: 0 4px 12px rgba(71, 109, 250, 0.1);
    transform: translateY(-1px);
  }
`;

const SurveyTypeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 10px;
  background: ${props => {
    if (props.primary) return '#f0f4ff';
    if (props.secondary) return '#fff4e6';
    return '#f8f9fa';
  }};
  color: ${props => {
    if (props.primary) return '#476dfa';
    if (props.secondary) return '#ff8c00';
    return '#7e7e7e';
  }};
  flex-shrink: 0;
`;

const SurveyTypeContent = styled.div`
  flex: 1;
`;

const SurveyTypeTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const SurveyTypeDescription = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
  line-height: 1.4;
`;

const SurveyTypeArrow = styled.div`
  color: #7e7e7e;
  opacity: 0.6;
  transition: all 0.2s;

  ${SurveyTypeOption}:hover & {
    color: #476dfa;
    opacity: 1;
    transform: translateX(2px);
  }
`;
